import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Palette, Upload, Globe, Type, MessageCircle, Save, Eye, Image, Sparkles, DollarSign, Gift, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TenantSettingsPage() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('branding');
  const fileRef = useRef(null);

  // Branding state
  const [name, setName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#D4AF37');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [supportEmail, setSupportEmail] = useState('');

  // Rules state
  const [rules, setRules] = useState({
    deposit_rules: { min_amount: 10, max_amount: 10000 },
    withdrawal_rules: { min_amount: 20, max_amount: 5000 },
    bonus_rules: { first_deposit_bonus_pct: 100, signup_bonus_fp: 50, signup_bonus_sc: 50, signup_bonus_xp: 50, referral_bonus_xp: 25 },
    support_channels: { whatsapp: '', telegram: '', messenger: '' },
  });

  useEffect(() => { fetchTenant(); }, []);

  const fetchTenant = async () => {
    try {
      const res = await api.get('/api/admin/my-tenant');
      const t = res.data.tenant;
      setTenant(t);
      setName(t.name || '');
      setPrimaryColor(t.branding?.primary_color || '#D4AF37');
      setSecondaryColor(t.branding?.secondary_color || '#10B981');
      setWelcomeMessage(t.branding?.welcome_message || '');
      setSupportEmail(t.settings?.support_email || '');
      const br = t.settings?.bonus_rules || {};
      const signupBonusFp = Number(br.signup_bonus_fp ?? br.signup_bonus_sc ?? br.signup_bonus_xp ?? 0);
      setRules({
        deposit_rules: t.settings?.deposit_rules || { min_amount: 10, max_amount: 10000 },
        withdrawal_rules: t.settings?.withdrawal_rules || { min_amount: 20, max_amount: 5000 },
        bonus_rules: {
          first_deposit_bonus_pct: br.first_deposit_bonus_pct ?? 100,
          signup_bonus_fp: signupBonusFp,
          signup_bonus_sc: signupBonusFp,
          signup_bonus_xp: signupBonusFp,
          referral_bonus_xp: br.referral_bonus_xp ?? 25,
          enabled: br.enabled ?? true,
        },
        support_channels: t.settings?.support_channels || { whatsapp: '', telegram: '', messenger: '' },
      });
    } catch { toast.error('Failed to load tenant info'); }
    finally { setLoading(false); }
  };

  const saveBranding = async () => {
    setSaving(true);
    try {
      await api.patch('/api/admin/my-tenant/branding', {
        name, primary_color: primaryColor, secondary_color: secondaryColor,
        welcome_message: welcomeMessage, support_email: supportEmail,
      });
      toast.success('Branding saved!');
      fetchTenant();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      // Signup bonus is canonical FP; legacy fields stay mirrored for old readers.
      const payload = { ...rules };
      if (payload.bonus_rules) {
        const signupBonusFp = Number(payload.bonus_rules.signup_bonus_fp ?? payload.bonus_rules.signup_bonus_sc ?? payload.bonus_rules.signup_bonus_xp ?? 0);
        payload.bonus_rules = {
          ...payload.bonus_rules,
          signup_bonus_fp: signupBonusFp,
          signup_bonus_sc: signupBonusFp,
          signup_bonus_xp: signupBonusFp,
        };
      }
      await api.patch('/api/admin/my-tenant/rules', payload);
      toast.success('Rules saved!');
      fetchTenant();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSavingRules(false); }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/admin/my-tenant/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded!');
      fetchTenant();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const getLogoUrl = () => {
    const url = tenant?.branding?.logo_url;
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const updateRule = (section, key, value) => {
    setRules(p => ({ ...p, [section]: { ...p[section], [key]: value } }));
  };

  const sections = [
    { id: 'branding', label: 'Branding & Theme', icon: Palette },
    { id: 'rules', label: 'Platform Rules', icon: DollarSign },
    { id: 'support', label: 'Support Channels', icon: MessageCircle },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-5" data-testid="tenant-settings-page">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#D4AF37]" /> Customize Your Platform</h2>
        <p className="text-sm text-gray-400 mt-0.5">Personalize branding, rules, and support for your players</p>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition ${activeSection === s.id ? 'bg-[#D4AF37]/10 text-[#D4AF37]' : 'text-gray-500 hover:text-gray-300'}`}
            data-testid={`section-${s.id}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* Branding Section */}
      {activeSection === 'branding' && (
        <div className="space-y-5" data-testid="branding-section">
          {/* Live Preview */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Eye className="w-4 h-4" /> Live Preview</h3>
            <div className="rounded-xl p-4" style={{ background: '#0B0E14', border: `2px solid ${primaryColor}30` }}>
              <div className="flex items-center gap-3 mb-3">
                {getLogoUrl() ? (
                  <img src={getLogoUrl()} alt="Logo" className="w-12 h-12 rounded-xl object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold" style={{ background: `${primaryColor}20`, color: primaryColor }}>
                    {name?.charAt(0) || 'T'}
                  </div>
                )}
                <div>
                  <h4 className="font-bold text-lg" style={{ color: primaryColor }}>{name || 'Your Tenant'}</h4>
                  <p className="text-xs text-gray-500">/{tenant?.slug}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <div className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: primaryColor, color: '#000' }}>Deposit</div>
                <div className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: secondaryColor, color: '#000' }}>Withdraw</div>
              </div>
            </div>
          </div>

          {/* Logo */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
            <h3 className="text-sm font-semibold text-gray-400 mb-3 flex items-center gap-2"><Image className="w-4 h-4" /> Logo</h3>
            <div className="flex items-center gap-4">
              {getLogoUrl() ? (
                <img src={getLogoUrl()} alt="Current logo" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-[#0B0E14] flex items-center justify-center text-gray-500 border border-white/10">
                  <Image className="w-6 h-6" />
                </div>
              )}
              <div>
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="border-white/10" data-testid="upload-logo-btn">
                  {uploading ? <div className="spinner w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                </Button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e.target.files?.[0])} />
                <p className="text-[10px] text-gray-500 mt-1">PNG, JPG. Recommended 200x200px</p>
              </div>
            </div>
          </div>

          {/* Name & Colors */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><Type className="w-4 h-4" /> Name & Colors</h3>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Platform Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="Your platform name" data-testid="tenant-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Primary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/10" />
                  <Input value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} className="bg-[#0B0E14] border-white/10 font-mono text-xs" data-testid="primary-color" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Secondary Color</label>
                <div className="flex gap-2">
                  <input type="color" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/10" />
                  <Input value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} className="bg-[#0B0E14] border-white/10 font-mono text-xs" data-testid="secondary-color" />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Welcome Message (shown on login)</label>
              <Input value={welcomeMessage} onChange={(e) => setWelcomeMessage(e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="Welcome to our platform!" data-testid="welcome-message" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Support Email</label>
              <Input value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="support@yourplatform.com" data-testid="support-email" />
            </div>
            <Button onClick={saveBranding} disabled={saving} className="bg-[#D4AF37] text-black w-full" data-testid="save-branding-btn">
              {saving ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </div>
        </div>
      )}

      {/* Rules Section */}
      {activeSection === 'rules' && (
        <div className="space-y-5" data-testid="rules-section">
          {/* Deposit Rules */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-green-400" /> Deposit Rules</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Min Deposit ($)</label>
                <Input type="number" value={rules.deposit_rules.min_amount} onChange={(e) => updateRule('deposit_rules', 'min_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="dep-min" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Max Deposit ($)</label>
                <Input type="number" value={rules.deposit_rules.max_amount} onChange={(e) => updateRule('deposit_rules', 'max_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="dep-max" />
              </div>
            </div>
          </div>

          {/* Withdrawal Rules */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-red-400" /> Withdrawal Rules ($ wallet)</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Min Withdrawal ($)</label>
                <Input type="number" value={rules.withdrawal_rules.min_amount} onChange={(e) => updateRule('withdrawal_rules', 'min_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="wit-min" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Max Withdrawal per Request ($)</label>
                <Input type="number" value={rules.withdrawal_rules.max_amount} onChange={(e) => updateRule('withdrawal_rules', 'max_amount', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="wit-max" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Max Withdrawals / Day (0 = unlimited)</label>
                <Input type="number" value={rules.withdrawal_rules.max_per_day ?? 0} onChange={(e) => updateRule('withdrawal_rules', 'max_per_day', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="wit-max-per-day" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Max Daily Total ($, 0 = unlimited)</label>
                <Input type="number" value={rules.withdrawal_rules.max_total_per_day ?? 0} onChange={(e) => updateRule('withdrawal_rules', 'max_total_per_day', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="wit-max-total-per-day" />
              </div>
            </div>
          </div>

          {/* Bonus Rules */}
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><Gift className="w-4 h-4 text-purple-400" /> Bonus Rules</h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">First Deposit Bonus %</label>
                <Input type="number" value={rules.bonus_rules.first_deposit_bonus_pct} onChange={(e) => updateRule('bonus_rules', 'first_deposit_bonus_pct', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="bonus-first-dep" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Signup Bonus (FP)</label>
                <Input type="number" value={rules.bonus_rules.signup_bonus_fp ?? 0} onChange={(e) => updateRule('bonus_rules', 'signup_bonus_fp', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="bonus-signup-fp" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Referral Bonus (FP)</label>
                <Input type="number" value={rules.bonus_rules.referral_bonus_xp} onChange={(e) => updateRule('bonus_rules', 'referral_bonus_xp', Number(e.target.value))} className="bg-[#0B0E14] border-white/10" data-testid="bonus-referral" />
              </div>
            </div>
          </div>

          <Button onClick={saveRules} disabled={savingRules} className="bg-[#D4AF37] text-black w-full" data-testid="save-rules-btn">
            {savingRules ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {savingRules ? 'Saving...' : 'Save Rules'}
          </Button>
        </div>
      )}

      {/* Support Channels Section */}
      {activeSection === 'support' && (
        <div className="space-y-5" data-testid="support-section">
          <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-400 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-blue-400" /> Support Channels</h3>
            <p className="text-xs text-gray-500">These links will show as icons in your players' chat tab</p>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#25D366"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2z" /></svg>
                WhatsApp Number
              </label>
              <Input value={rules.support_channels.whatsapp} onChange={(e) => updateRule('support_channels', 'whatsapp', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="+1234567890" data-testid="support-whatsapp" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#0088CC"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" /></svg>
                Telegram Username / Link
              </label>
              <Input value={rules.support_channels.telegram} onChange={(e) => updateRule('support_channels', 'telegram', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="@yourtelegram or https://t.me/..." data-testid="support-telegram" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="#006AFF"><path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.18.16.15.26.36.27.58l.05 1.81c.02.63.66 1.03 1.24.78l2.02-.89c.17-.08.36-.1.55-.06.88.24 1.82.37 2.72.37 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2z" /></svg>
                Messenger Link
              </label>
              <Input value={rules.support_channels.messenger} onChange={(e) => updateRule('support_channels', 'messenger', e.target.value)} className="bg-[#0B0E14] border-white/10" placeholder="https://m.me/..." data-testid="support-messenger" />
            </div>
            <Button onClick={saveRules} disabled={savingRules} className="bg-[#D4AF37] text-black w-full" data-testid="save-support-btn">
              {savingRules ? <div className="spinner w-4 h-4 mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {savingRules ? 'Saving...' : 'Save Support Channels'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
